import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Dropdown, Button, Empty, Input, Select, Modal, message, Flex, Spin } from "antd";
import {
  MoreOutlined,
  SearchOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FilterOutlined,
  HistoryOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  TimesheetContainer,
  PageHeader,
  TopSection,
  TabsContainer,
  TableContainer,
  EmptyState,
  MetricsSection,
  MetricCard,
} from "./Timesheet.style";
import TimesheetDetailsModal from "./TimesheetDetailsModal";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const getMonthName = (monthNum) => {
  const months = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];
  return months[monthNum - 1] || "";
};

const transformTimesheetData = (apiData) => {
  return apiData.map(ts => ({
    id: ts.id,
    talentId: ts.talentId,
    talentName: ts.talentName || ts.talentId,
    role: ts.role || "N/A",
    partnerOrg: ts.partnerOrganization,
    clientName: ts.customerOrganization,
    monthlyRate: parseFloat(ts.monthlyRate) || 0,
    partnerRate: parseFloat(ts.partnerRate) || 0,
    settledCost: parseFloat(ts.settledCost) || 0,
    clientRate: parseFloat(ts.clientRate) || 0,
    partnerAmount: parseFloat(ts.partnerAmount) || 0,
    settledAmount: parseFloat(ts.settledAmount) || 0,
    month: ts.monthName || getMonthName(ts.month),
    monthNum: ts.month,
    year: String(ts.year),
    actualWorkingDays: ts.totalWorkingDays,
    leaveTaken: { 
      count: (ts.paidLeaveCount || 0) + (ts.unpaidLeaveCount || 0), 
      dates: ts.leaveDates || [] 
    },
    paidLeave: ts.paidLeaveCount || 0,
    unpaidLeave: ts.unpaidLeaveCount || 0,
    workingDays: ts.actualWorkingDays,
    billableHours: parseFloat(ts.billableHours) || 0,
    calculatedAmount: parseFloat(ts.calculatedAmount) || 0,
    status: ts.status,
    timesheetFileUrl: ts.timesheetFileUrl,
    submittedDate: ts.submittedAt ? new Date(ts.submittedAt).toISOString().split('T')[0] : null,
    verifiedDate: ts.verifiedAt ? new Date(ts.verifiedAt).toISOString().split('T')[0] : null,
    approvedDate: ts.approvedAt ? new Date(ts.approvedAt).toISOString().split('T')[0] : null,
    rejectionReason: ts.rejectionReason,
    hiringRecordId: ts.hiringRecordId,
    jobId: ts.jobId,
    workingHoursPerDay: ts.workingHoursPerDay || 8,
  }));
};

const Timesheet = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Pending Upload");
  const [searchText, setSearchText] = useState("");
  const [filterPartner, setFilterPartner] = useState(null);
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [showPastTimesheets, setShowPastTimesheets] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [modalMode, setModalMode] = useState("edit");
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterYear) body.year = parseInt(filterYear);
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      
      const response = await axios.post(API_CONST.GET_TIMESHEETS, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.timesheets) {
        const transformed = transformTimesheetData(response.data.timesheets);
        setTimesheets(transformed);
        
        const partners = [...new Set(transformed.map(t => t.partnerOrg))].filter(Boolean);
        setPartnerOptions(partners);
      }
    } catch (error) {
      console.error("Error fetching timesheets:", error);
      message.error("Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  const handleGenerateTimesheets = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      const response = await axios.post(API_CONST.GENERATE_TIMESHEETS, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.created !== undefined) {
        message.success(`Generated ${response.data.created} timesheets for ${getMonthName(now.getMonth() + 1)} ${now.getFullYear()} (${response.data.skipped} already existed)`);
        fetchTimesheets();
      }
    } catch (error) {
      console.error("Error generating timesheets:", error);
      message.error(error.response?.data?.message || "Failed to generate timesheets");
    } finally {
      setGenerating(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      const response = await axios.post(API_CONST.SYNC_NOW, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { timesheets: tsResult, invoices: invResult } = response.data;
      message.success(`Sync complete: ${tsResult.generated} timesheets generated, ${invResult.generated} invoices created`);
      fetchTimesheets();
    } catch (error) {
      console.error("Error syncing:", error);
      message.error(error.response?.data?.message || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  const getFilteredData = () => {
    let filtered = timesheets;

    if (!showPastTimesheets) {
      if (activeTab === "Pending Upload") {
        filtered = filtered.filter(t => t.status === "pending" || t.status === "submitted" || t.status === "rejected");
      } else if (activeTab === "Verified") {
        filtered = filtered.filter(t => t.status === "verified");
      } else if (activeTab === "Approved") {
        filtered = filtered.filter(t => t.status === "approved");
      }
    } else {
      filtered = filtered.filter(t => t.status === "approved");
    }

    if (filterStatus) {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    if (filterPartner) {
      filtered = filtered.filter(t => t.partnerOrg === filterPartner);
    }

    if (searchText) {
      filtered = filtered.filter(t =>
        t.talentName.toLowerCase().includes(searchText.toLowerCase()) ||
        t.clientName.toLowerCase().includes(searchText.toLowerCase()) ||
        t.role.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  };

  const currentData = getFilteredData();

  const getMetrics = () => {
    const pending = timesheets.filter(t => t.status === "pending" || t.status === "submitted" || t.status === "rejected").length;
    const verified = timesheets.filter(t => t.status === "verified").length;
    const rejected = timesheets.filter(t => t.status === "rejected").length;
    const approved = timesheets.filter(t => t.status === "approved").length;
    const totalAmountPending = timesheets
      .filter(t => t.status === "pending" || t.status === "submitted" || t.status === "rejected")
      .reduce((sum, t) => sum + t.calculatedAmount, 0);
    const totalAmountApproved = timesheets
      .filter(t => t.status === "approved")
      .reduce((sum, t) => sum + t.calculatedAmount, 0);

    return { pending, verified, rejected, approved, totalAmountPending, totalAmountApproved };
  };

  const metrics = getMetrics();

  const handleUploadClick = (record) => {
    setSelectedTalent(record);
    setModalMode("edit");
    setUploadModalVisible(true);
  };

  const handleUploadSubmit = async ({ leaveDates, paidLeaveCount, timesheetFile }) => {
    try {
      const token = localStorage.getItem("authToken");
      
      let timesheetFileUrl = selectedTalent.timesheetFileUrl;
      if (timesheetFile) {
        const uploadResponse = await axios.post(API_CONST.TIMESHEET_UPLOAD_REQUEST_URL, {
          timesheetId: selectedTalent.id,
          talentName: selectedTalent.talentName,
          name: timesheetFile.name,
          size: timesheetFile.size,
          contentType: timesheetFile.type
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (uploadResponse.data.uploadURL) {
          await axios.put(uploadResponse.data.uploadURL, timesheetFile, {
            headers: { "Content-Type": timesheetFile.type }
          });
          timesheetFileUrl = uploadResponse.data.objectPath;
        }
      }

      const isAlreadySubmitted = selectedTalent.status === 'submitted' || selectedTalent.status === 'verified';
      const endpoint = isAlreadySubmitted 
        ? `${API_CONST.UPDATE_TIMESHEET}/${selectedTalent.id}`
        : `${API_CONST.SUBMIT_TIMESHEET}/${selectedTalent.id}`;

      const response = await axios.put(endpoint, {
        leaveDates,
        paidLeaveCount,
        timesheetFileUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.timesheet) {
        message.success(`Timesheet ${isAlreadySubmitted ? 'updated' : 'submitted'} successfully for ${selectedTalent.talentName}`);
        fetchTimesheets();
        setUploadModalVisible(false);
        setSelectedTalent(null);
      }
    } catch (error) {
      console.error("Error submitting timesheet:", error);
      message.error(error.response?.data?.message || "Failed to submit timesheet");
    }
  };

  const handleShare = async (record) => {
    Modal.confirm({
      title: "Share Timesheet",
      content: `Share ${record.talentName}'s timesheet with ${record.clientName}?`,
      okText: "Share",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.post(`${API_CONST.SHARE_TIMESHEET}/${record.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.message) {
            message.success(`Timesheet shared with ${record.clientName}`);
          }
        } catch (error) {
          console.error("Error sharing timesheet:", error);
          message.error(error.response?.data?.message || "Failed to share timesheet");
        }
      },
    });
  };

  const handleDownload = async (record) => {
    if (!record.timesheetFileUrl) {
      message.warning("No timesheet file available for download");
      return;
    }
    
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(API_CONST.GET_TIMESHEET_DOWNLOAD_URL, {
        timesheetFileUrl: record.timesheetFileUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error("Error getting download URL:", error);
      message.error("Failed to download timesheet");
    }
  };

  const handleVerify = (record) => {
    Modal.confirm({
      title: "Verify Timesheet",
      content: `Verify timesheet for ${record.talentName} (${record.month})? This will make it available for approval.`,
      okText: "Verify",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.put(`${API_CONST.VERIFY_TIMESHEET}/${record.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.timesheet) {
            message.success(`Timesheet verified for ${record.talentName}`);
            fetchTimesheets();
          }
        } catch (error) {
          console.error("Error verifying timesheet:", error);
          message.error(error.response?.data?.message || "Failed to verify timesheet");
        }
      },
    });
  };

  const handleApprove = (record) => {
    Modal.confirm({
      title: "Approve Timesheet",
      content: `Approve timesheet for ${record.talentName} (${record.month})?`,
      okText: "Approve",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.put(`${API_CONST.APPROVE_TIMESHEET}/${record.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.timesheet) {
            message.success(`Timesheet approved for ${record.talentName}`);
            fetchTimesheets();
          }
        } catch (error) {
          console.error("Error approving timesheet:", error);
          message.error(error.response?.data?.message || "Failed to approve timesheet");
        }
      },
    });
  };

  const handleModify = (record) => {
    setSelectedTalent(record);
    setModalMode("edit");
    setUploadModalVisible(true);
  };

  const handleView = (record) => {
    setSelectedTalent(record);
    setModalMode("view");
    setUploadModalVisible(true);
  };

  const handleReject = (record) => {
    let rejectionReason = "";
    Modal.confirm({
      title: "Reject Timesheet",
      content: (
        <div>
          <p>Reject timesheet for {record.talentName} ({record.month})?</p>
          <Input.TextArea
            id="reject-reason"
            placeholder="Enter reason for rejection"
            rows={3}
            style={{ marginTop: 12 }}
            onChange={(e) => { rejectionReason = e.target.value; }}
          />
        </div>
      ),
      okText: "Reject",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.put(`${API_CONST.REJECT_TIMESHEET}/${record.id}`, {
            rejectionReason
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.timesheet) {
            message.warning(`Timesheet rejected for ${record.talentName}`);
            fetchTimesheets();
          }
        } catch (error) {
          console.error("Error rejecting timesheet:", error);
          message.error(error.response?.data?.message || "Failed to reject timesheet");
        }
      },
    });
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete Timesheet",
      content: `Are you sure you want to delete timesheet for ${record.talentName} (${record.month})? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.delete(`${API_CONST.DELETE_TIMESHEET}/${record.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.message) {
            message.success(`Timesheet deleted for ${record.talentName}`);
            fetchTimesheets();
          }
        } catch (error) {
          console.error("Error deleting timesheet:", error);
          message.error(error.response?.data?.message || "Failed to delete timesheet");
        }
      },
    });
  };

  const getActionMenu = (record) => {
    if (showPastTimesheets) {
      return [
        {
          key: "view",
          label: "View",
          onClick: () => handleView(record),
        },
        {
          key: "download",
          label: "Download Timesheet",
          onClick: () => handleDownload(record),
        },
      ];
    }

    const adminItems = [
      {
        key: "modify",
        label: "Modify",
        onClick: () => handleModify(record),
      },
      {
        key: "reject",
        label: "Reject",
        onClick: () => handleReject(record),
        danger: true,
      },
      {
        key: "delete",
        label: "Delete",
        onClick: () => handleDelete(record),
        danger: true,
      },
    ];

    const baseItems = [
      {
        key: "view",
        label: "View",
        onClick: () => handleView(record),
      },
      {
        key: "download",
        label: "Download Timesheet",
        onClick: () => handleDownload(record),
      },
    ];

    if (record.status === "pending") {
      return [
        {
          key: "upload",
          label: "Upload Timesheet",
          onClick: () => handleUploadClick(record),
        },
        ...baseItems,
        ...adminItems,
      ];
    } else if (record.status === "submitted") {
      return [
        {
          key: "verify",
          label: "Verify",
          icon: <CheckCircleOutlined />,
          onClick: () => handleVerify(record),
        },
        ...baseItems,
        ...adminItems,
      ];
    } else if (record.status === "verified") {
      return [
        {
          key: "approve",
          label: "Approve",
          icon: <CheckOutlined />,
          onClick: () => handleApprove(record),
        },
        {
          key: "share",
          label: "Share with Client",
          onClick: () => handleShare(record),
        },
        ...baseItems,
        ...adminItems,
      ];
    } else {
      return [
        {
          key: "share",
          label: "Share with Client",
          onClick: () => handleShare(record),
        },
        ...baseItems,
        ...adminItems,
      ];
    }
  };

  const columns = [
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: "13%",
      render: (text, record) => record.talentId ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          className="talent-name"
          onClick={() => navigate(`/admin/talent-details/${record.talentId}`)}
        >
          {text}
        </a>
      ) : <span className="talent-name">{text}</span>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: "13%",
    },
    {
      title: "Partner",
      dataIndex: "partnerOrg",
      key: "partnerOrg",
      width: "12%",
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/timesheet", fromLabel: "Back to Timesheet" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      width: "11%",
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/timesheet", fromLabel: "Back to Timesheet" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: "9%",
      render: (text, record) => (
        <span className="date-text">
          {text}
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            ({record.actualWorkingDays} days)
          </div>
        </span>
      ),
    },
    {
      title: "Leave Taken",
      key: "leaveTaken",
      width: "9%",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.leaveTaken.count} days</div>
          {record.leaveTaken.count > 0 && record.leaveTaken.dates.length > 0 && (
            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
              {record.leaveTaken.dates.slice(0, 2).map(d => parseInt(d.split('-')[2], 10)).join(', ')}
              {record.leaveTaken.dates.length > 2 && '...'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Working Days",
      dataIndex: "workingDays",
      key: "workingDays",
      width: "9%",
      render: (days) => <span style={{ fontWeight: 500 }}>{days} days</span>,
    },
    {
      title: "Billable Hours",
      dataIndex: "billableHours",
      key: "billableHours",
      width: "9%",
      render: (hours) => <span style={{ fontWeight: 500 }}>{hours} hrs</span>,
    },
    {
      title: "Partner Rate",
      dataIndex: "partnerAmount",
      key: "partnerAmount",
      width: 130,
      align: /** @type {'right'} */ ('right'),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#014c75", whiteSpace: "nowrap" }}>₹ {Math.round(amount || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Settled Cost",
      dataIndex: "settledAmount",
      key: "settledAmount",
      width: 130,
      align: /** @type {'right'} */ ('right'),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#ff9800", whiteSpace: "nowrap" }}>₹ {Math.round(amount || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Client Rate",
      dataIndex: "calculatedAmount",
      key: "calculatedAmount",
      width: 130,
      align: /** @type {'right'} */ ('right'),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#00d9a9", whiteSpace: "nowrap" }}>₹ {Math.round(amount || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "7%",
      render: (_, record) => (
        <span className={`status-badge ${record.status}`}>
          {record.status === "pending" && "Pending"}
          {record.status === "submitted" && "Submitted"}
          {record.status === "verified" && "Verified"}
          {record.status === "approved" && "Approved"}
          {record.status === "rejected" && "Rejected"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "5%",
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenu(record) }} trigger={["click"]}>
          <button className="icon-button menu-btn">
            <MoreOutlined />
          </button>
        </Dropdown>
      ),
    },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <TimesheetContainer>
      <PageHeader>
        <h1>Timesheet Management</h1>
        <p>Upload and manage timesheets for onboarded talents</p>
      </PageHeader>

      <MetricsSection>
        <MetricCard>
          <div className="metric-label">Pending Upload</div>
          <div className="metric-value">{metrics.pending}</div>
        </MetricCard>

        <MetricCard>
          <div className="metric-label">Approved</div>
          <div className="metric-value">{metrics.approved}</div>
        </MetricCard>

        <MetricCard>
          <div className="metric-label">Total Amount Pending</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmountPending / 100000).toFixed(2)}L
          </div>
        </MetricCard>

        <MetricCard>
          <div className="metric-label">Total Amount Approved</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmountApproved / 100000).toFixed(2)}L
          </div>
        </MetricCard>
      </MetricsSection>

      <TopSection>
        <Flex align="start" justify="space-between" style={{ marginBottom: 20 }}>
          <Flex gap="middle">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by talent, client, or role"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Year
                </span>
              }
              value={filterYear}
              onChange={setFilterYear}
              style={{ width: 120 }}
              allowClear
            >
              {yearOptions.map(year => (
                <Option key={year} value={String(year)}>{year}</Option>
              ))}
            </Select>
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Month
                </span>
              }
              value={filterMonth}
              onChange={setFilterMonth}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="January">January</Option>
              <Option value="February">February</Option>
              <Option value="March">March</Option>
              <Option value="April">April</Option>
              <Option value="May">May</Option>
              <Option value="June">June</Option>
              <Option value="July">July</Option>
              <Option value="August">August</Option>
              <Option value="September">September</Option>
              <Option value="October">October</Option>
              <Option value="November">November</Option>
              <Option value="December">December</Option>
            </Select>
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Partner
                </span>
              }
              value={filterPartner}
              onChange={setFilterPartner}
              style={{ width: 200 }}
              allowClear
            >
              {partnerOptions.map(partner => (
                <Option key={partner} value={partner}>{partner}</Option>
              ))}
            </Select>
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Status
                </span>
              }
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="pending">Pending</Option>
              <Option value="submitted">Submitted</Option>
              <Option value="verified">Verified</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </Flex>
          <Flex gap="small">
            <Button
              type="primary"
              icon={<SyncOutlined spin={generating} />}
              onClick={handleGenerateTimesheets}
              loading={generating}
              style={{
                backgroundColor: "#00d9a9",
                borderColor: "#00d9a9",
                height: "32px",
                fontWeight: 500,
              }}
            >
              Generate Monthly Timesheets
            </Button>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setShowPastTimesheets(!showPastTimesheets)}
              style={{
                backgroundColor: showPastTimesheets ? "#00d9a9" : "#fff",
                color: showPastTimesheets ? "#fff" : "#014c75",
                borderColor: showPastTimesheets ? "#00d9a9" : "#d9d9d9",
                height: "32px",
                fontWeight: 500,
              }}
            >
              {showPastTimesheets ? "Current View" : "View All History"}
            </Button>
          </Flex>
        </Flex>
      </TopSection>

      {!showPastTimesheets && (
        <TabsContainer>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              className={`tab-button ${activeTab === "Pending Upload" ? "active" : ""}`}
              onClick={() => setActiveTab("Pending Upload")}
            >
              Pending Upload ({metrics.pending})
            </Button>
            <Button
              className={`tab-button ${activeTab === "Verified" ? "active" : ""}`}
              onClick={() => setActiveTab("Verified")}
            >
              Verified ({metrics.verified})
            </Button>
            <Button
              className={`tab-button ${activeTab === "Approved" ? "active" : ""}`}
              onClick={() => setActiveTab("Approved")}
            >
              Approved ({metrics.approved})
            </Button>
          </div>
          <Button
            type="link"
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSyncNow}
            loading={syncing}
            style={{ padding: 0, color: '#1890ff' }}
          >
            Sync Now
          </Button>
        </TabsContainer>
      )}

      <TableContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : currentData.length > 0 ? (
          <Table
            columns={columns}
            dataSource={currentData}
            rowKey="id"
            pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
              pageSize: 10,
              showTotal: (total) => `Total ${total} timesheets`,
            }}
          />
        ) : (
          <EmptyState>
            <Empty description={`No ${activeTab.toLowerCase()} timesheets found`} />
          </EmptyState>
        )}
      </TableContainer>

      <TimesheetDetailsModal
        visible={uploadModalVisible}
        timesheet={selectedTalent}
        mode={modalMode}
        userRole="Admin"
        onClose={() => {
          setUploadModalVisible(false);
          setSelectedTalent(null);
        }}
        onSubmit={handleUploadSubmit}
      />
    </TimesheetContainer>
  );
};

export default Timesheet;
